<template>
  <!-- py はダークブロック内部の padding（セクション間余白ではない）。区切り余白は親 wrapper の pb が担うため二重 bump しない -->
  <section class="bg-black6 px-4 py-12 md:py-20" data-testid="wabunka-story-section">
    <div class="mx-auto md:w-11/12 md:max-w-[1216px]">
      <!-- セクションヘッダー: 左寄せ gold タイトル + 矢印 + gold 水平線、サブタイトル
           Figma 仕様: PC は単数 "Wabunka Story"、SP は複数形 "Wabunka Stories" でサブタイトル文言も異なる
           タイトル + 矢印アイコンは `/stories/` へのリンク (FeaturedSection / 共通 ExperiencesSection の見出しと同じパターン) -->
      <div class="mb-6 flex flex-col gap-y-2 md:mb-8">
        <div class="flex items-center gap-x-2">
          <NuxtLink class="flex items-center gap-x-2 hover:opacity-80" data-testid="wabunka-story-title-link" to="/stories/" @click="handleClickViewAll">
            <!-- Figma: タイトルは Foreground/accent (#A48953 = gold4)
                 サイズは他のトップページセクション (Destinations / RecentlyViewed 等) と揃えるため SP=20px / PC=30px (Figma h2) とする -->
            <h2 class="font-Spectral text-xl font-medium tracking-[0.5px] text-gold4 md:text-[30px] md:leading-[40px]">
              <span class="md:hidden">Wabunka Stories</span>
              <span class="hidden md:inline">Wabunka Story</span>
            </h2>
            <img alt="" aria-hidden="true" class="h-6 w-6 shrink-0" :src="arrowRightGold500Icon" />
          </NuxtLink>
          <!-- Figma: 横線は PC のみ表示。SP はタイトル + 矢印アイコンのみ -->
          <span aria-hidden="true" class="ml-2 hidden h-px flex-grow bg-gold4 md:block" />
        </div>
        <!-- Figma: SP サブタイトルは Foreground/muted (#A7A199 = gray8)、PC は white -->
        <p class="font-Spectral text-base font-light leading-6 text-gray8 md:text-[17px] md:text-white">
          <span class="md:hidden">Delve deeper into the rich experiences and beliefs of our cultural experts</span>
          <span class="hidden md:inline">Interviews that touch on the background of the experience, and Stories that give a deeper insight into Japan.</span>
        </p>
      </div>

      <!-- Desktop: Z字配置 (左に大カード(1件) + 右に 2x2 小カード(4件)) -->
      <div class="hidden w-full md:grid md:grid-cols-2 md:gap-2">
        <NuxtLink
          v-if="largeStory"
          :key="largeStory.id"
          class="group relative aspect-[3/4] w-full overflow-hidden bg-black1"
          :data-testid="`story-card-${largeStory.id}`"
          :to="`/stories/${largeStory.id}/`"
        >
          <StoryImage :alt="`${largeStory.title}の画像`" :src="largeStory.thumbnailImage.url" />
          <StoryCaption variant="large" :provider-name="largeStory.provider.name" :title="largeStory.title" />
        </NuxtLink>

        <div class="grid grid-cols-2 gap-2">
          <NuxtLink
            v-for="story in smallStories"
            :key="story.id"
            class="group relative aspect-[3/4] w-full overflow-hidden bg-black1"
            :data-testid="`story-card-${story.id}`"
            :to="`/stories/${story.id}/`"
          >
            <StoryImage :alt="`${story.title}の画像`" :src="story.thumbnailImage.url" />
            <StoryCaption variant="small" :provider-name="story.provider.name" :title="story.title" />
          </NuxtLink>
        </div>
      </div>

      <!-- Mobile: 横スクロールカルーセル (5枚すべて 280px で統一、共通の HorizontalScrollWrapper を使用。スクロールバーは OS 標準＝スクロール中のみ表示で他セクションと統一) -->
      <div class="-mx-4 md:hidden">
        <MoleculesHorizontalScrollWrapper>
          <NuxtLink
            v-for="(story, i) in displayStories"
            :key="story.id"
            class="group flex w-[280px] flex-shrink-0 flex-col"
            :class="{ 'ml-4': i === 0, 'mr-4': i === displayStories.length - 1 }"
            :data-testid="`story-card-${story.id}`"
            :to="`/stories/${story.id}/`"
          >
            <div class="relative aspect-[3/4] w-full overflow-hidden bg-black1">
              <OptimizedImage class="h-full w-full object-cover" loading="lazy" :alt="`${story.title}の画像`" :image-url="story.thumbnailImage.url" :optimized-image-options="{ width: 640 }" />
            </div>
            <div class="mt-3 flex flex-col gap-y-1 px-1 text-white">
              <p class="font-Spectral text-xs font-light leading-4">{{ story.provider.name }}</p>
              <p class="line-clamp-2 font-Spectral text-sm font-medium leading-5 tracking-[0.5px]">{{ story.title }}</p>
            </div>
          </NuxtLink>
        </MoleculesHorizontalScrollWrapper>
      </div>

      <div class="mt-8 flex justify-center md:mt-12">
        <!-- NuxtLink ベース: prop 未指定時もナビゲーションは必ず動作する。onClickViewAll は GA 等の副作用専用
             Figma: ボタン背景は Background/accent (gold-400 + 右側に向かう微グラデ)、文字色は Foreground/inversed
             共通 PrimaryButton (dark theme) と同じく plugin の `.dark-bg-accent` (グラデ付き) と `text-dark-fg-inverse` を使う -->
        <NuxtLink
          class="flex h-12 w-full max-w-[368px] items-center justify-center px-6 font-Spectral text-[17px] text-dark-fg-inverse transition-opacity dark-bg-accent hover:opacity-90"
          data-testid="view-all-stories-btn"
          to="/stories/"
          @click="handleClickViewAll"
        >
          <span class="md:hidden">View stories</span>
          <span class="hidden md:inline">View all stories</span>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import arrowRightGold500Icon from '@/assets/icon/arrow/right-gold500.svg';
import MoleculesHorizontalScrollWrapper from '@/components/molecules/HorizontalScrollWrapper/HorizontalScrollWrapper.vue';
import type { MinimalEnStoryListViewModel, MinimalEnStoryViewModel } from '@/models/wabunka/enStories';
import OptimizedImage from '@/wabunka-src/common/components/stateful/OptimizedImage/OptimizedImage.vue';
import StoryCaption from '@/wabunka-src/features/Top/pages/components/StorySection/StoryCaption.vue';
import StoryImage from '@/wabunka-src/features/Top/pages/components/StorySection/StoryImage.vue';

// Figma 仕様: 5件 (大1 + 小4)
const MAX_DISPLAY_STORIES = 5;

interface Props {
  enStories: MinimalEnStoryListViewModel;
  onClickViewAll?: () => void;
}

const props = defineProps<Props>();

const displayStories = computed<MinimalEnStoryListViewModel>(() => props.enStories.slice(0, MAX_DISPLAY_STORIES));

// Z字配置 (Figma 注釈:「最新のStoryが左（一番大きく表示）上から2→3 下3→4の更新順で動的に変わる」):
// 1=largeStory(左大) / 2-5=smallStories(右側 2x2 を Z 順で埋める)
const largeStory = computed<MinimalEnStoryViewModel | undefined>(() => displayStories.value[0]);
const smallStories = computed<MinimalEnStoryListViewModel>(() => displayStories.value.slice(1, MAX_DISPLAY_STORIES));

// onClickViewAll は optional。NuxtLink 側でナビゲーションは必ず動くため、ここでは GA 等の副作用呼び出しのみを担う
const handleClickViewAll = () => {
  props.onClickViewAll?.();
};
</script>
